import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Home, KeyRound, HeartHandshake, Quote } from "lucide-react";
import { WELCOME, TEAM, TESTIMONIALS, TESTIMONIAL_BG, FEATURES } from "../mock";

const ICONS = { Users, Home, KeyRound, HeartHandshake };

function TeamCard({ member }) {
  const [expanded, setExpanded] = useState(false);
  const paragraphs = member.bio.split("\n\n");
  const hasMore = paragraphs.length > 1 || paragraphs[0].length > 160;

  return (
    <div
      data-testid={`team-member-${member.name.toLowerCase()}`}
      className="group flex flex-col items-center rounded-[1.75rem] border border-[#e5ddd0] bg-[#faf7f0] p-8 text-center shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg"
    >
      {member.photoHover ? (
        <div className="h-24 w-24 [perspective:700px]">
          <div className="relative h-full w-full transition-transform duration-700 ease-out [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
            <div className="absolute inset-0 overflow-hidden rounded-full shadow-md ring-4 ring-white [backface-visibility:hidden]">
              <img src={member.photo} alt={member.name} className="h-full w-full object-cover" style={{ objectPosition: member.imgPos || "center 20%" }} />
            </div>
            <div className="absolute inset-0 overflow-hidden rounded-full shadow-md ring-4 ring-white [backface-visibility:hidden] [transform:rotateY(180deg)]">
              <img src={member.photoHover} alt={member.name} className="h-full w-full object-cover" style={{ objectPosition: member.imgPosHover || "center 20%" }} />
            </div>
          </div>
        </div>
      ) : member.photo ? (
        <div className="h-24 w-24 overflow-hidden rounded-full shadow-md ring-4 ring-white">
          <img src={member.photo} alt={member.name} className="h-full w-full object-cover" style={{ objectPosition: member.imgPos || "center 20%" }} />
        </div>
      ) : (
        <div
          className="grid h-24 w-24 place-items-center rounded-full font-display text-3xl font-600 text-white shadow-md ring-4 ring-white transition-transform duration-300 group-hover:scale-105"
          style={{ backgroundColor: member.accent }}
        >
          {member.initials}
        </div>
      )}
      <h3 className="mt-6 font-display text-2xl font-500 text-[#16323f]">{member.name}</h3>
      <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#c9a25e]">{member.role}</p>

      {expanded ? (
        <div className="mt-4 space-y-3 text-[14px] leading-relaxed text-[#5b5450]">
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      ) : (
        <p className="mt-4 line-clamp-4 text-[14px] leading-relaxed text-[#5b5450]">{paragraphs[0]}</p>
      )}

      {hasMore && (
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid={`team-toggle-${member.name.toLowerCase()}`}
          className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#ec008c] transition-colors hover:text-[#c60074]"
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
}

export default function About() {
  return (
    <>
      {/* Welcome */}
      <section id="about" className="bg-[#faf7f0] py-16 lg:py-28">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-5 lg:grid-cols-5 lg:gap-16">
          <div className="relative lg:col-span-3">
            <div className="absolute -left-4 -top-4 hidden h-full w-full rounded-[2rem] border border-[#d8b074]/60 lg:block" />
            <div className="relative overflow-hidden rounded-[2rem] shadow-xl">
              <img
                src={WELCOME.image}
                onError={(e) => (e.currentTarget.src = WELCOME.fallback)}
                alt="New Space office"
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </div>
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">Independent &middot; Boutique &middot; Local</p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-5xl">
              {WELCOME.title}
            </h2>
            <span className="mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-[#5b5450]">
              {WELCOME.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section id="team" className="bg-[#f3ede3] py-16 lg:py-24" data-testid="team-section">
        <div className="mx-auto max-w-[1200px] px-5">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">The People Behind New Space</p>
            <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
              {TEAM.title}
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
            <p className="mt-6 text-[15px] leading-relaxed text-[#5b5450]">{TEAM.intro}</p>
          </div>
          <div className="mt-14 grid items-start gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {TEAM.members.map((m) => (
              <TeamCard key={m.name} member={m} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative overflow-hidden py-24">
        <img
          src={TESTIMONIAL_BG}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#16323f]/80" />
        <div className="relative z-10 mx-auto grid max-w-[1100px] gap-10 px-5 md:grid-cols-2">
          {TESTIMONIALS.map((t) => (
            <div key={t.author} className="text-white">
              <Quote size={44} className="mb-4 text-[#ec008c]" fill="#ec008c" />
              <p className="text-lg font-light leading-relaxed">{t.quote}</p>
              <p className="mt-4 font-display text-xl font-500 text-[#eaa32a]">{t.author}</p>
            </div>
          ))}
        </div>
        <div className="relative z-10 mt-10 text-center">
          <Link
            to="/testimonials"
            className="inline-block rounded-full border border-white/70 px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-[#16323f]"
          >
            Read more
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#f3ede3] py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-5">
          <div className="mb-14 text-center">
            <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
              Why landlords choose us
            </h2>
            <span className="mx-auto mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => {
              const Icon = ICONS[f.icon];
              return (
                <div
                  key={f.title}
                  className="group rounded-[1.75rem] border border-[#e5ddd0] bg-[#faf7f0] p-8 text-center shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg"
                >
                  <div
                    className="mx-auto grid h-20 w-20 place-items-center rounded-full text-white shadow-md transition-transform duration-300 group-hover:scale-105"
                    style={{ backgroundColor: f.color }}
                  >
                    <Icon size={34} strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-500 text-[#16323f]">
                    {f.title}
                  </h3>
                  <p className="mt-4 text-[14px] leading-relaxed text-[#5b5450]">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
